import { Injectable, signal, DOCUMENT, inject } from '@angular/core';
import { PopoverController } from '@ionic/angular/standalone';

import { SelectThemeComponent } from '@app/components/select-theme/select-theme.component';
import { StorageService } from '@app/services/storage.service';
import { inlineTry } from '@app/utils/functions.helper';

export enum ThemePreference {
  System = 'system',
  Light = 'light',
  Dark = 'dark',
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private storage = inject(StorageService);
  private popoverCtrl = inject(PopoverController);
  private document = inject<Document>(DOCUMENT);

  // !!! The theme key and class are duplicated in index.html (inline script vs. FOUC).
  // When changing, update both places synchronously.
  themeKey = 'theme_preference';
  readonly darkClass = 'ion-palette-dark';
  readonly themes = Object.values(ThemePreference);
  // Labels for the theme options in the German UI
  readonly themeLabels: Record<ThemePreference, string> = {
    [ThemePreference.System]: 'System',
    [ThemePreference.Light]: 'Hell',
    [ThemePreference.Dark]: 'Dunkel',
  };
  readonly theme = signal<ThemePreference>(ThemePreference.System);

  private media = window.matchMedia('(prefers-color-scheme: dark)');

  constructor() {
    // Keep the palette in sync with the OS setting when "system" is selected
    this.media.addEventListener('change', () => {
      if (this.theme() === ThemePreference.System) {
        this.applySystemTheme();
      }
    });
  }

  async init() {
    const readStoredTheme = await this.storage.get<ThemePreference>(this.themeKey);
    const theme = readStoredTheme && this.themes.includes(readStoredTheme) ? readStoredTheme : ThemePreference.System;
    this.theme.set(theme);
    this.applyTheme(theme);
  }

  setTheme(theme: ThemePreference): void {
    this.theme.set(theme);
    inlineTry(() => this.storage.set(this.themeKey, theme));
    this.applyTheme(theme);
  }

  async openPopupSetTheme(ev?: Event) {
    const popover = await this.popoverCtrl.create({
      component: SelectThemeComponent,
      event: ev,
    });
    await popover.present();
  }

  private applyTheme(theme: ThemePreference): void {
    if (theme === ThemePreference.System) {
      this.applySystemTheme();
    } else {
      this.document.documentElement.classList.toggle(this.darkClass, theme === ThemePreference.Dark);
    }
  }

  private applySystemTheme(): void {
    this.document.documentElement.classList.toggle(this.darkClass, this.media.matches);
  }
}
